import { useRef } from 'react';
import { Settings } from 'lucide-react';
import ChannelList, { type ChannelRowDnd } from '../channels/ChannelList';
import { ChannelRow } from '../channels/ChannelRow';
import { ChannelListSkeleton } from '../ui/skeletons';
import FitText from '../ui/FitText';
import type { Channel as ListChannel, ChannelFolder as ListFolder } from '../../types/channel';

// Mobile gesture-aware Channels / DM drawer (the small-screen sibling of the
// desktop channels column). Structural subsets of ChatLayout's types.
type Space = { id: string; name?: string | null; avatarUrl?: string | null };
type Channel = { id: string; name?: string | null; type?: string };
type Friend = { id: string; name?: string; username?: string };

type MobileChannelsSheetProps = {
  // Layout / drawer animation
  channelPaneView: 'spaces' | 'dms';
  isDmSpace: boolean;
  swipePanel: null | 'chan' | 'users';
  chanDrawerProgress: number;
  // Header
  currentSpace?: { name?: string | null } | null;
  voids: Space[];
  currentVoidId: string;
  headerSpaceLabel: string;
  currentSpaceOnlineCount: number;
  // DM list
  activeDmVoids: Space[];
  dmFriendSearch: string;
  setDmFriendSearch: (value: string) => void;
  filteredFriendsForDmSearch: Friend[];
  dmStartBusyUserId: string | null;
  unread: Record<string, number>;
  getDmDisplayName: (space?: { id?: string; name?: string | null } | null, fallbackId?: string) => string;
  // Channels
  channels: Channel[];
  currentChannelId: string;
  /** The channels the user can see, ordered, and the space's folders: the same data the desktop column renders. */
  sidebarChannels: ListChannel[];
  /** Channels may still be arriving: show skeleton rows instead of "No channels yet". */
  loading?: boolean;
  folders: ListFolder[];
  canManageChannels: boolean;
  getVoiceNamesForChannel: (channelId: string) => string[];
  // Callbacks
  onClose: () => void;
  onOpenDm: (voidId: string) => void;
  onStartDm: (userId: string) => void | Promise<void>;
  onSwitchChannel: (channelId: string) => void;
  onChannelReorder: (channelId: string, targetFolderId: string | null, beforeChannelId: string | null) => void;
  onFolderReorder: (folderId: string, beforeFolderId: string | null) => void;
  onOpenChannelSettings: () => void;
};

function MobileChannelsSheet({
  channelPaneView,
  isDmSpace,
  swipePanel,
  chanDrawerProgress,
  currentSpace,
  voids,
  currentVoidId,
  headerSpaceLabel,
  currentSpaceOnlineCount,
  activeDmVoids,
  dmFriendSearch,
  setDmFriendSearch,
  filteredFriendsForDmSearch,
  dmStartBusyUserId,
  unread,
  getDmDisplayName,
  channels,
  currentChannelId,
  sidebarChannels,
  loading = false,
  folders,
  canManageChannels,
  getVoiceNamesForChannel,
  onClose,
  onOpenDm,
  onStartDm,
  onSwitchChannel,
  onChannelReorder,
  onFolderReorder,
  onOpenChannelSettings,
}: MobileChannelsSheetProps) {
  const listScrollRef = useRef<HTMLDivElement>(null);

  // The same row as the desktop column; press and hold to drag it (see ChannelList / useSlotReorder).
  const renderChannel = (ch: ListChannel, dnd: ChannelRowDnd) => {
    // Channel ids here are already fully-qualified (`space:channel`); guard the rare short-id case so we never build a
    // doubled `space:space:channel` key that always misses.
    const unreadKey = String(ch.id).includes(':') ? ch.id : `${currentVoidId}:${ch.id}`;
    const active = ch.id === currentChannelId;
    const voiceNames = ch.type === 'voice' ? getVoiceNamesForChannel(ch.id) : [];
    return (
      <ChannelRow
        channel={ch}
        active={active}
        unread={!active && (unread[unreadKey] || 0) > 0}
        dnd={dnd}
        onSelect={() => onSwitchChannel(ch.id)}
        voiceUsers={voiceNames}
        voiceNames={voiceNames}
      />
    );
  };

  return (
    <div className="md:hidden fixed inset-0 z-40">
      <div
        className="absolute inset-0 bg-black/70"
        style={{
          opacity: chanDrawerProgress * 0.6,
          transition: 'opacity 220ms ease-out',
          pointerEvents: chanDrawerProgress > 0.01 ? 'auto' : 'none',
        }}
        onClick={onClose}
      />
      <div
        className="absolute inset-y-0 left-0 w-4/5 max-w-sm mobile-sheet border-r border-white/10 shadow-lg shadow-black/60 flex flex-col"
        style={{
          transform: `translateX(-${(1 - chanDrawerProgress) * 100}%)`,
          transition: swipePanel === 'chan' ? 'none' : 'transform 320ms ease-out',
          willChange: 'transform',
        }}
      >
        <div className="px-5 pt-4 pb-3 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="min-w-0">
              <p className="text-xs text-fg-3">{channelPaneView === 'dms' ? 'Direct messages' : (isDmSpace ? 'Conversation' : 'Channels')}</p>
              <p className="flex min-w-0 items-baseline text-white">
                <FitText className="font-medium" maxRem={1.125} minRem={0.8}>{channelPaneView === 'dms' ? 'Direct messages' : (isDmSpace ? (currentSpace?.name || 'Direct messages') : (voids.find((v) => v.id === currentVoidId)?.name || headerSpaceLabel))}</FitText>
              {!isDmSpace && currentSpaceOnlineCount > 0 && (
                <span className="ml-2 shrink-0 whitespace-nowrap text-sm text-online-light/80">• {currentSpaceOnlineCount} online</span>
              )}
            </p>
          </div>
            <button className="rounded-full border border-white/10 p-2 text-fg-2 hover:text-white" onClick={onClose} aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
            <div ref={listScrollRef} className="flex-1 overflow-auto px-5 py-4 space-y-4">
              {channelPaneView === 'dms' ? (
                <div>
                  <div className="text-xs text-fg-3">Active direct messages</div>
                  <ul className="mt-2 space-y-1.5">
                    {activeDmVoids.length === 0 ? (
                      <li className="rounded-2xl border border-white/10 bg-white/5 p-3 space-y-2">
                        <input
                          value={dmFriendSearch}
                          onChange={(e) => setDmFriendSearch(e.target.value)}
                          placeholder="Search friends to message..."
                          aria-label="Search friends to message"
                          className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none placeholder:text-fg-3 focus:border-emerald-500/60"
                        />
                        <div className="space-y-1.5 max-h-64 overflow-auto">
                          {filteredFriendsForDmSearch.map((f) => (
                            <button
                              key={`dm-search-mobile-drawer-${f.id}`}
                              className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-left text-sm text-fg hover:border-emerald-500/40 disabled:opacity-60"
                              disabled={dmStartBusyUserId === f.id}
                              onClick={() => { void onStartDm(f.id); }}
                            >
                              {f.name || f.username || 'Friend'}
                            </button>
                          ))}
                          {filteredFriendsForDmSearch.length === 0 && (
                            <div className="rounded-xl border border-white/5 bg-black/20 px-3 py-2 text-xs text-fg-3">
                              {dmFriendSearch.trim() ? 'No friends match that search.' : 'No friends available yet.'}
                            </div>
                          )}
                        </div>
                      </li>
                    ) : (
                      activeDmVoids.map((v) => {
                        const count = unread[`${v.id}:chat`] || 0;
                        const active = currentVoidId === v.id;
                        return (
                          <li key={v.id} className="flex items-center gap-2">
                            <button
                              className={`flex-1 rounded-2xl border px-3 py-2 text-left transition ${active ? 'border-emerald-500/60 bg-emerald-500/10 text-white' : 'border-white/5 bg-white/5 text-fg hover:border-white/15'}`}
                              onClick={() => onOpenDm(v.id)}
                            >
                              {getDmDisplayName(v, v.id)}
                            </button>
                            {count > 0 && (
                              <span className="min-w-6 rounded-full bg-emerald-600 px-2 py-1 text-center text-xs font-semibold text-white">{count > 99 ? '99+' : count}</span>
                            )}
                          </li>
                        );
                      })
                    )}
                  </ul>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-fg-3">{isDmSpace ? 'Conversation' : 'Channels'}</div>
                    {!isDmSpace && canManageChannels && (
                      <button
                        type="button"
                        className="rounded-full border border-white/10 p-1.5 text-fg-2 hover:text-white"
                        onClick={onOpenChannelSettings}
                        aria-label="Channel settings"
                        title="Channel settings"
                      >
                        <Settings className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {isDmSpace ? (
                    <ul className="mt-2 space-y-1.5">
                      {channels
                        .filter((ch) => ch.id === 'chat')
                        .map((ch) => {
                          const active = ch.id === currentChannelId;
                          return (
                            <li key={ch.id}>
                              <button
                                className={`w-full rounded-2xl border px-3 py-2 text-left transition ${
                                  active ? 'border-emerald-500/60 bg-emerald-500/10 text-white' : 'border-white/5 bg-white/5 text-fg hover:border-white/15'
                                }`}
                                onClick={() => onSwitchChannel(ch.id)}
                              >
                                {ch.name}
                              </button>
                            </li>
                          );
                        })}
                    </ul>
                  ) : (
                    <div className="mt-2 -mx-2">
                      {sidebarChannels.length === 0 && loading ? (
                        <ChannelListSkeleton />
                      ) : sidebarChannels.length === 0 ? (
                        <p className="px-2 py-3 text-sm text-fg-3">No channels yet.</p>
                      ) : (
                        <ChannelList
                          channels={sidebarChannels}
                          folders={folders}
                          scrollRef={listScrollRef}
                          renderChannel={renderChannel}
                          onChannelReorder={onChannelReorder}
                          onFolderReorder={onFolderReorder}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
      </div>
    </div>
  );
}

export default MobileChannelsSheet;
